import { CapabilityButton } from "../CapabilityButton";
import { permissionNames as P } from "../../auth/permissions.generated";
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Separator } from '../ui/separator';
import { 
  PlayCircle, 
  StopCircle, 
  RefreshCw, 
  Calendar,
  Database,
  Clock,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  Info,
  AlertTriangle,
  Skull
} from 'lucide-react';
import { useHubSpotBackfillApi, type HubSpotBackfillRequest, type HubSpotBackfillStatus } from '../../services/apiService';
import { useSharedPermissions } from '../../contexts/PermissionsContext';

interface BackfillJob {
  instanceId: string;
  status: string;
  startDate: string;
  endDate: string;
  chunkSize: string;
  startTime: string;
  lastUpdated: string;
  progress?: number;
  totalContacts?: number;
  completedChunks?: number;
  totalChunks?: number;
  failedChunks?: number;
  duration?: number;
  success?: boolean;
  errorMessage?: string;
  consecutiveFailures?: number; // Track repeated API failures
  markedForRemoval?: boolean;   // Flag for cleanup logic
  chunkResults?: Array<{
    startDate: string;
    endDate: string;
    contactsProcessed: number;
    success: boolean;
    duration: number;
    errorMessage?: string;
  }>;
}

const CHUNK_SIZE_OPTIONS = [
  { value: 'Month', label: 'Month', description: 'Process data in monthly chunks (recommended for recent data)' },
  { value: 'Year', label: 'Year', description: 'Process data in yearly chunks (better for large historical ranges)' }
];

const STATUS_COLORS = {
  'Pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Running': 'bg-blue-100 text-blue-800 border-blue-200',
  'Completed': 'bg-green-100 text-green-800 border-green-200',
  'Failed': 'bg-red-100 text-red-800 border-red-200',
  'Terminated': 'bg-gray-100 text-gray-800 border-gray-200',
  'Unknown': 'bg-orange-100 text-orange-800 border-orange-200'
};

const STATUS_ICONS = {
  'Pending': AlertCircle,
  'Running': RefreshCw,
  'Completed': CheckCircle,
  'Failed': XCircle,
  'Terminated': StopCircle,
  'Unknown': AlertTriangle
};

export function HubSpotBackfilling() {
  const permissions = useSharedPermissions();
  const hubspotApi = useHubSpotBackfillApi();

  // Form state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1); // Default to 1 year ago
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [chunkSize, setChunkSize] = useState<'Month' | 'Year'>('Month');

  // UI state
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isKillingAll, setIsKillingAll] = useState(false);
  const [killError, setKillError] = useState<string | null>(null);
  const [killSuccess, setKillSuccess] = useState<string | null>(null);
  const [cleanupInfo, setCleanupInfo] = useState<string | null>(null);

  // Active jobs tracking
  const [activeJobs, setActiveJobs] = useState<BackfillJob[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Permission check
  const canManageHubSpot = permissions.canManageHubSpot || permissions.isAdmin;

  useEffect(() => {
    if (canManageHubSpot) {
      refreshActiveJobs();
      
      // Set up polling for active jobs
      const interval = setInterval(() => {
        if (activeJobs.some(job => job.status === 'Running' || job.status === 'Pending')) {
          refreshActiveJobs();
        }
      }, 5000); // Poll every 5 seconds

      return () => clearInterval(interval);
    }
  }, [canManageHubSpot, activeJobs.length]);

  const refreshActiveJobs = async () => {
    if (!canManageHubSpot) return;
    
    setIsRefreshing(true);
    try {
      // For now, we'll need to track active jobs in local storage or state
      // In a real implementation, you might want to add an endpoint to list active jobs
      const storedJobs = localStorage.getItem('hubspot-backfill-jobs');
      if (storedJobs) {
        const jobs: BackfillJob[] = JSON.parse(storedJobs);
        
        // Update status for each job, but filter out jobs that have been consistently failing to connect
        const updatedJobs = await Promise.all(
          jobs.map(async (job) => {
            try {
              const status = await hubspotApi.getBackfillStatus(job.instanceId);
              
              // Log the status for debugging
              console.log(`Status for job ${job.instanceId.substring(0, 8)}:`, {
                runtimeStatus: status.runtimeStatus,
                orchestrationData: status.orchestrationData,
                hasOrchestrationData: !!status.orchestrationData,
                output: status.output
              });
              
              // Extract orchestration data if available
              const orchestrationData = status.orchestrationData;
              
              // Create updated job with proper handling of orchestration data
              const updatedJob = {
                ...job,
                status: status.runtimeStatus,
                lastUpdated: new Date().toISOString(),
                // Reset consecutive failures since we got a successful response
                consecutiveFailures: 0
              };

              // Only update these fields if orchestration data is available and has valid values
              if (orchestrationData && typeof orchestrationData === 'object') {
                // Update totalContacts if available (including 0 as a valid value)
                if (typeof orchestrationData.totalContacts === 'number') {
                  updatedJob.totalContacts = orchestrationData.totalContacts;
                }
                
                // Update completedChunks if available (including 0 as a valid value)
                if (typeof orchestrationData.completedChunks === 'number') {
                  updatedJob.completedChunks = orchestrationData.completedChunks;
                }
                
                // Update totalChunks if available (including 0 as a valid value)
                if (typeof orchestrationData.totalChunks === 'number') {
                  updatedJob.totalChunks = orchestrationData.totalChunks;
                }
                
                // Calculate progress if we have both values
                if (typeof orchestrationData.totalChunks === 'number' && 
                    typeof orchestrationData.completedChunks === 'number' && 
                    orchestrationData.totalChunks > 0) {
                  updatedJob.progress = Math.round((orchestrationData.completedChunks / orchestrationData.totalChunks) * 100);
                }
                
                // Update additional orchestration data
                if (typeof orchestrationData.failedChunks === 'number') {
                  updatedJob.failedChunks = orchestrationData.failedChunks;
                }
                
                if (typeof orchestrationData.duration === 'number') {
                  updatedJob.duration = orchestrationData.duration;
                }
                
                if (typeof orchestrationData.success === 'boolean') {
                  updatedJob.success = orchestrationData.success;
                }
                
                if (orchestrationData.errorMessage) {
                  updatedJob.errorMessage = orchestrationData.errorMessage;
                }
                
                if (Array.isArray(orchestrationData.chunkResults)) {
                  updatedJob.chunkResults = orchestrationData.chunkResults;
                }
              }

              console.log(`Updated job ${job.instanceId.substring(0, 8)}:`, {
                totalContacts: updatedJob.totalContacts,
                completedChunks: updatedJob.completedChunks,
                totalChunks: updatedJob.totalChunks,
                progress: updatedJob.progress,
                status: updatedJob.status
              });

              return updatedJob;
            } catch (error) {
              console.error(`Failed to get status for job ${job.instanceId}:`, error);
              
              // Track consecutive failures for cleanup logic
              const consecutiveFailures = (job.consecutiveFailures || 0) + 1;
              
              // Handle specific case where orchestration is not found (404)
              if (error instanceof Error && error.message.includes('404')) {
                console.log(`Orchestration ${job.instanceId.substring(0, 8)} not found (404) - likely completed and cleaned up. Marking as completed.`);
                
                // Mark as completed if it was previously running and now not found
                // This happens when orchestrations are automatically cleaned up after completion
                return {
                  ...job,
                  status: job.status === 'Running' || job.status === 'Pending' ? 'Completed' : job.status,
                  lastUpdated: new Date().toISOString(),
                  consecutiveFailures: 0, // Reset since we understand why it failed
                  // Keep existing data if job was already completed
                  ...(job.status === 'Running' || job.status === 'Pending' ? {} : {
                    totalContacts: job.totalContacts,
                    completedChunks: job.completedChunks,
                    totalChunks: job.totalChunks,
                    progress: job.progress,
                    failedChunks: job.failedChunks,
                    duration: job.duration,
                    success: job.success,
                    errorMessage: job.errorMessage,
                    chunkResults: job.chunkResults
                  })
                };
              }
              
              // For other errors, increment failure count and potentially mark for removal
              if (consecutiveFailures >= 5) {
                console.log(`Job ${job.instanceId.substring(0, 8)} has failed ${consecutiveFailures} times consecutively. Marking as stale for removal.`);
                return {
                  ...job,
                  status: 'Unknown',
                  lastUpdated: new Date().toISOString(),
                  consecutiveFailures: consecutiveFailures,
                  markedForRemoval: true
                };
              }
              
              // For other errors, mark as unknown status but keep the job
              return { 
                ...job, 
                status: 'Unknown', 
                lastUpdated: new Date().toISOString(),
                consecutiveFailures: consecutiveFailures
              };
            }
          })
        );

        // Filter out jobs based on multiple criteria:
        // 1. Age-based cleanup (older than 7 days and not active)
        // 2. Consecutive failure cleanup (marked for removal due to repeated failures)
        // 3. Completed job cleanup (completed jobs older than 1 day)
        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const oneDayAgo = new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000);
        
        const filteredJobs = updatedJobs.filter(job => {
          const jobDate = new Date(job.lastUpdated);
          const isRecent = jobDate > sevenDaysAgo;
          const isActive = job.status === 'Running' || job.status === 'Pending';
          const isCompleted = job.status === 'Completed' || job.status === 'Failed' || job.status === 'Terminated';
          const isRecentlyCompleted = isCompleted && jobDate > oneDayAgo;
          const isMarkedForRemoval = job.markedForRemoval === true;
          
          // Remove if:
          // 1. Job is marked for removal due to repeated failures
          // 2. Job is completed and older than 1 day
          // 3. Job is not active and older than 7 days
          if (isMarkedForRemoval) {
            console.log(`Removing job ${job.instanceId.substring(0, 8)} due to repeated failures`);
            return false;
          }
          
          if (isCompleted && !isRecentlyCompleted) {
            console.log(`Removing completed job ${job.instanceId.substring(0, 8)} older than 1 day`);
            return false;
          }
          
          if (!isActive && !isRecent) {
            console.log(`Removing inactive job ${job.instanceId.substring(0, 8)} older than 7 days`);
            return false;
          }
          
          // Keep job if:
          // 1. It's active (regardless of age)
          // 2. It's recently completed (within 1 day)
          // 3. It's not too old (within 7 days) and not failed repeatedly
          return isActive || isRecentlyCompleted || isRecent;
        });

        // Log cleanup info if jobs were removed
        if (filteredJobs.length < updatedJobs.length) {
          const cleanedCount = updatedJobs.length - filteredJobs.length;
          console.log(`Cleaned up ${cleanedCount} old/stale jobs from localStorage`);
          setCleanupInfo(`Automatically cleaned up ${cleanedCount} old or stale job${cleanedCount > 1 ? 's' : ''}`);
          
          // Clear cleanup message after 5 seconds
          setTimeout(() => setCleanupInfo(null), 5000);
        }

        setActiveJobs(filteredJobs);
        localStorage.setItem('hubspot-backfill-jobs', JSON.stringify(filteredJobs));
      }
    } catch (error) {
      console.error('Failed to refresh active jobs:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleStartBackfill = async () => {
    if (!canManageHubSpot) {
      setError('You do not have permission to manage HubSpot backfilling');
      return;
    }

    setIsStarting(true);
    setError(null);
    setSuccess(null);

    try {
      // Only send defined values and remove undefined/null
      const request: HubSpotBackfillRequest = {
        startDate: startDate ? new Date(startDate).toISOString() : undefined,
        endDate: endDate ? new Date(endDate).toISOString() : undefined,
        chunkSize: chunkSize || undefined
      };
      
      // Filter out undefined/null values manually for better compatibility
      const cleanRequest: HubSpotBackfillRequest = {};
      if (request.startDate !== undefined) cleanRequest.startDate = request.startDate;
      if (request.endDate !== undefined) cleanRequest.endDate = request.endDate;
      if (request.chunkSize !== undefined) cleanRequest.chunkSize = request.chunkSize;

      const response = await hubspotApi.startBackfill(cleanRequest);

      // Add to active jobs
      const newJob: BackfillJob = {
        instanceId: response.instanceId,
        status: 'Pending',
        startDate: request.startDate!,
        endDate: request.endDate!,
        chunkSize: request.chunkSize!,
        startTime: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      };

      const updatedJobs = [...activeJobs, newJob];
      setActiveJobs(updatedJobs);
      localStorage.setItem('hubspot-backfill-jobs', JSON.stringify(updatedJobs));

      setSuccess(`Backfill started successfully! Instance ID: ${response.instanceId}`);
    } catch (error) {
      console.error('Failed to start backfill:', error);
      setError(error instanceof Error ? error.message : 'Failed to start backfill');
    } finally {
      setIsStarting(false);
    }
  };

  const handleKillAllBackfills = async () => {
    if (!canManageHubSpot) {
      setKillError('You do not have permission to manage HubSpot backfilling');
      return;
    }

    // Show confirmation dialog
    const confirmMessage = 'Are you sure you want to kill ALL running HubSpot backfill processes? This action cannot be undone.';
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsKillingAll(true);
    setKillError(null);
    setKillSuccess(null);

    try {
      const response = await hubspotApi.terminateAllBackfills('Emergency termination by user');

      // Update local storage to mark terminated jobs
      const storedJobs = localStorage.getItem('hubspot-backfill-jobs');
      if (storedJobs) {
        const jobs: BackfillJob[] = JSON.parse(storedJobs);
        const updatedJobs = jobs.map(job => {
          // Mark running/pending jobs as terminated
          if (job.status === 'Running' || job.status === 'Pending') {
            return { ...job, status: 'Terminated', lastUpdated: new Date().toISOString() };
          }
          return job;
        });
        setActiveJobs(updatedJobs);
        localStorage.setItem('hubspot-backfill-jobs', JSON.stringify(updatedJobs));
      }

      setKillSuccess(`Successfully terminated ${response.summary.terminated} backfill processes. ${response.summary.failed} failed to terminate.`);
      
      // Refresh active jobs to get updated status
      setTimeout(() => refreshActiveJobs(), 1000);
    } catch (error) {
      console.error('Failed to kill all backfills:', error);
      setKillError(error instanceof Error ? error.message : 'Failed to terminate backfill processes');
    } finally {
      setIsKillingAll(false);
    }
  };

  const handleKillBackfill = async (instanceId: string) => {
    if (!canManageHubSpot) {
      setError('You do not have permission to manage HubSpot backfilling');
      return;
    }

    // Show confirmation dialog
    const confirmMessage = `Are you sure you want to kill the backfill process ${instanceId.substring(0, 8)}...? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await hubspotApi.terminateBackfill(instanceId, 'Terminated by user');

      // Update the job status in local storage and state
      const updatedJobs = activeJobs.map(job => {
        if (job.instanceId === instanceId) {
          return { ...job, status: 'Terminated', lastUpdated: new Date().toISOString() };
        }
        return job;
      });
      
      setActiveJobs(updatedJobs);
      localStorage.setItem('hubspot-backfill-jobs', JSON.stringify(updatedJobs));

      setSuccess(`Successfully terminated backfill process ${instanceId.substring(0, 8)}...`);
      
      // Refresh active jobs after a short delay
      setTimeout(() => refreshActiveJobs(), 1000);
    } catch (error) {
      console.error(`Failed to kill backfill ${instanceId}:`, error);
      setError(error instanceof Error ? error.message : 'Failed to terminate backfill process');
    }
  };

  const handleClearCompletedJobs = () => {
    const runningJobs = activeJobs.filter(job => job.status === 'Running' || job.status === 'Pending');
    setActiveJobs(runningJobs);
    localStorage.setItem('hubspot-backfill-jobs', JSON.stringify(runningJobs));
    
    const clearedCount = activeJobs.length - runningJobs.length;
    if (clearedCount > 0) {
      setSuccess(`Cleared ${clearedCount} completed job${clearedCount > 1 ? 's' : ''} from the list`);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleClearAllJobs = () => {
    // Confirmation dialog
    const confirmMessage = 'Are you sure you want to clear ALL jobs from the list? This will only clear the local display and will not affect running orchestrations.';
    if (!window.confirm(confirmMessage)) {
      return;
    }

    const clearedCount = activeJobs.length;
    setActiveJobs([]);
    localStorage.removeItem('hubspot-backfill-jobs');
    
    if (clearedCount > 0) {
      setSuccess(`Cleared all ${clearedCount} job${clearedCount > 1 ? 's' : ''} from the list`);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const formatDuration = (startTime: string, endTime?: string) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const duration = end.getTime() - start.getTime();
    
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  const getStatusIcon = (status: string) => {
    const IconComponent = STATUS_ICONS[status as keyof typeof STATUS_ICONS] || AlertCircle;
    return <IconComponent className="h-4 w-4" />;
  };

  if (!canManageHubSpot) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-6 ml-0 lg:ml-64">
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight">HubSpot Backfilling</h1>
          <p className="text-muted-foreground">
            Manage HubSpot contact data backfilling processes
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                You do not have permission to access HubSpot management features. 
                Please contact your administrator to request the 'manage:hubspot' permission.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Separate active and completed jobs
  const activeJobsList = activeJobs.filter(job => job.status === 'Running' || job.status === 'Pending');
  const completedJobsList = activeJobs.filter(job => job.status !== 'Running' && job.status !== 'Pending');

  return (
    <div className="container mx-auto max-w-5xl px-4 py-6 ml-0 lg:ml-64">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">HubSpot Backfilling</h1>
        <p className="text-muted-foreground">
          Manage HubSpot contact data backfilling processes with parallel chunk processing
        </p>
      </div>

      {/* Start New Backfill */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PlayCircle className="h-5 w-5" />
            Start New Backfill
          </CardTitle>
          <CardDescription>
            Configure and start a new HubSpot contact backfill process
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="chunkSize">Chunk Size</Label>
              <Select value={chunkSize} onValueChange={(value: 'Month' | 'Year') => setChunkSize(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select chunk size" />
                </SelectTrigger>
                <SelectContent>
                  {CHUNK_SIZE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div>
                        <div className="font-medium">{option.label}</div>
                        <div className="text-sm text-muted-foreground">{option.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              The backfill process will retrieve HubSpot contacts for the specified date range using parallel processing. 
              Choose "Month" for recent data (faster) or "Year" for large historical ranges (more efficient for big datasets).
            </AlertDescription>
          </Alert>

          {error && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <CapabilityButton capability={P.HubspotBackfillCreate}
              className="flex-1" 
              onClick={handleStartBackfill}
              disabled={isStarting}
            >
              {isStarting ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Starting Backfill...
                </>
              ) : (
                <>
                  <PlayCircle className="mr-2 h-4 w-4" />
                  Start Backfill
                </>
              )}
            </CapabilityButton>
            
            {/* Emergency Kill All Button */}
            {activeJobsList.length > 0 && (
              <CapabilityButton capability={P.HubspotBackfillCancel}
                variant="destructive" 
                onClick={handleKillAllBackfills}
                disabled={isKillingAll}
                className="bg-red-600 hover:bg-red-700"
              >
                {isKillingAll ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Killing...
                  </>
                ) : (
                  <>
                    <Skull className="mr-2 h-4 w-4" />
                    Kill All
                  </>
                )}
              </CapabilityButton>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Active Jobs */}
      {activeJobsList.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Active Backfill Jobs ({activeJobsList.length})
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={refreshActiveJobs}
                disabled={isRefreshing}
              >
                {isRefreshing ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </CardTitle>
            <CardDescription>
              Currently running or pending backfill processes
            </CardDescription>
          </CardHeader>
          <CardContent>
            {killError && (
              <Alert variant="destructive" className="mb-4">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{killError}</AlertDescription>
              </Alert>
            )}

            {killSuccess && (
              <Alert className="mb-4">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>{killSuccess}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              {activeJobsList.map((job) => (
                <Card key={job.instanceId} className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-3 flex-1">
                        <div className="flex items-center gap-3">
                          <Badge className={STATUS_COLORS[job.status as keyof typeof STATUS_COLORS] || 'bg-gray-100'}>
                            {getStatusIcon(job.status)}
                            <span className="ml-1">{job.status}</span>
                          </Badge>
                          <code className="text-sm bg-muted px-2 py-1 rounded">
                            {job.instanceId.substring(0, 8)}...
                          </code>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Users className="h-3 w-3" />
                            <span className="font-medium">{job.totalContacts?.toLocaleString() || '0'} contacts</span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <div className="font-medium flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Period
                            </div>
                            <div className="text-muted-foreground">
                              {new Date(job.startDate).toLocaleDateString()} - {new Date(job.endDate).toLocaleDateString()}
                            </div>
                          </div>
                          
                          <div>
                            <div className="font-medium">Chunks</div>
                            <div className="text-muted-foreground">
                              {job.completedChunks || 0} / {job.totalChunks || 0}
                              {(job.failedChunks || 0) > 0 && (
                                <span className="text-red-600 ml-1">({job.failedChunks} failed)</span>
                              )}
                            </div>
                          </div>
                          
                          <div>
                            <div className="font-medium flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Duration
                            </div>
                            <div className="text-muted-foreground">
                              {formatDuration(job.startTime)}
                            </div>
                          </div>
                          
                          <div>
                            <div className="font-medium">Chunk Size</div>
                            <div className="text-muted-foreground">{job.chunkSize}</div>
                          </div>
                        </div>

                        {job.totalChunks && job.completedChunks !== undefined && job.totalChunks > 0 && (
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>Progress: {job.completedChunks}/{job.totalChunks} chunks</span>
                              <span>{Math.round((job.completedChunks / job.totalChunks) * 100)}%</span>
                            </div>
                            <Progress value={(job.completedChunks / job.totalChunks) * 100} className="h-2" />
                          </div>
                        )}
                      </div>
                      
                      <CapabilityButton capability={P.HubspotBackfillCancel}
                        variant="outline"
                        size="sm"
                        onClick={() => handleKillBackfill(job.instanceId)}
                        className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 ml-4"
                      >
                        <StopCircle className="h-4 w-4 mr-1" />
                        Kill
                      </CapabilityButton>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Jobs History */}
      {completedJobsList.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Recent Jobs ({completedJobsList.length})
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleClearCompletedJobs}
                  className="text-gray-600 border-gray-200 hover:bg-gray-50"
                >
                  <Database className="h-4 w-4 mr-1" />
                  Clear Completed
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleClearAllJobs}
                  className="text-orange-600 border-orange-200 hover:bg-orange-50"
                >
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
              </div>
            </CardTitle>
            <CardDescription>
              Recently completed, failed, or terminated backfill processes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {completedJobsList.map((job) => (
                <div key={job.instanceId} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge className={STATUS_COLORS[job.status as keyof typeof STATUS_COLORS] || 'bg-gray-100'}>
                      {getStatusIcon(job.status)}
                      <span className="ml-1">{job.status}</span>
                    </Badge>
                    <code className="text-sm bg-muted px-2 py-1 rounded">
                      {job.instanceId.substring(0, 8)}...
                    </code>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Users className="h-3 w-3" />
                      <span>{job.totalContacts?.toLocaleString() || '0'} contacts</span>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {formatDuration(job.startTime, job.lastUpdated)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cleanup Info */}
      {cleanupInfo && (
        <Alert className="mb-6">
          <Info className="h-4 w-4" />
          <AlertDescription>{cleanupInfo}</AlertDescription>
        </Alert>
      )}

      {/* Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            Backfill Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-2">Chunk Size Guidelines</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• <strong>Month:</strong> Best for recent data (last 1-3 years)</li>
                <li>• <strong>Year:</strong> Optimal for large historical ranges (5+ years)</li>
                <li>• Parallel processing improves performance</li>
                <li>• Each chunk respects HubSpot API rate limits</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-1">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                Job Management
              </h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• <strong>Auto Cleanup:</strong> Completed jobs removed after 1 day</li>
                <li>• <strong>Stale Detection:</strong> Jobs with 5+ consecutive failures are removed</li>
                <li>• <strong>Clear Completed:</strong> Manually remove finished jobs</li>
                <li>• <strong>Clear All:</strong> Remove all jobs from display (emergency cleanup)</li>
                <li>• <strong>?? Note:</strong> Clearing jobs only affects display, not running orchestrations</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
